import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import {
  enqueue,
  listAll,
  listForCase,
  newClientKey,
  queueStorageAvailable,
  recordAttempt,
  remove,
  sweepExpired,
  type QueuedUpload,
} from '@/lib/uploadQueue'

/* Draining the durable upload queue (Story 8.2, UX-DR-19, AD-18).
 *
 * The persistence is in lib/uploadQueue; this is the half that SENDS. The two
 * are separate because they fail differently: storage fails per device and per
 * browser, sending fails per request and per link, and a module that mixed them
 * would have one error path for two unrelated problems.
 *
 * The loop is a singleton across the whole application rather than one per
 * mounted component. That is the point of the module-level state below: the AC
 * requires queued files to send themselves on reconnect, which must hold while
 * the user is on the dashboard, on another case, or on no case at all. A
 * per-component loop would stop the moment the user navigated away from the
 * file area — precisely when they believe they are free to.
 *
 * The three-step (AD-18) is the same one Story 8.1 established. What is added
 * is that every attempt carries the SAME clientKey, so an attempt that
 * ambiguously failed — the commit request left, the response did not come back
 * — creates no second file when it is retried. The server's per-case unique
 * index turns the duplicate into a lookup that returns the first row.
 */

interface UploadTicket {
  objectKey: string
  url: string
  expiresAt: string
  maxBytes: number
  method: string
}

interface FileResponse {
  id: string
  fileName: string
  contentType: string
  byteSize: number
  uploadedById: string
  uploadedByName: string
  uploadedAt: string
}

/* ── the store the UI subscribes to ────────────────────────────────────────── */

/* Queue state lives in module scope, not in a React context, and this is worth
 * defending. The queue is not per-subtree state: it belongs to the tab. A
 * context provider would need mounting above every consumer, and a case screen
 * rendered outside it would silently show no pending files — the exact failure
 * ("nothing appears complete when it is not") the last AC forbids.
 *
 * The snapshot is REPLACED rather than mutated on every change, because
 * useSyncExternalStore compares snapshots by identity: mutating in place would
 * leave subscribers rendering stale counts with no way to notice. */
let snapshot: readonly QueuedUpload[] = []
const listeners = new Set<() => void>()

function publish(next: readonly QueuedUpload[]) {
  snapshot = next
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): readonly QueuedUpload[] {
  return snapshot
}

/* The server snapshot is a stable frozen empty array rather than a fresh `[]`.
 * A new array each call is a new identity each call, which makes
 * useSyncExternalStore throw its infinite-loop error during SSR or any
 * hydration pass. */
const EMPTY: readonly QueuedUpload[] = Object.freeze([])
function getServerSnapshot(): readonly QueuedUpload[] {
  return EMPTY
}

/** Re-reads the queue from storage and notifies subscribers. */
async function refresh(): Promise<void> {
  if (!queueStorageAvailable()) return
  try {
    publish(await listAll())
  } catch {
    // A device that cannot read its queue still shows whatever it last knew,
    // rather than blanking the pending list and implying the work is done.
  }
}

/* ── the drain ────────────────────────────────────────────────────────────── */

/** Guards against two drains running at once.
 *
 *  Two concurrent drains would send the same entry twice. The idempotency key
 *  means the SERVER survives that — but both attempts would still push the same
 *  bytes over a link that could barely carry them once, which is the resource
 *  this whole story is trying not to waste. */
let draining = false

/** Set when a drain is asked for while one is already running, so the loop runs
 *  once more afterwards instead of dropping the request. Without this, a file
 *  added mid-drain would sit until the NEXT trigger. */
let drainRequested = false

/** Attempts after which an entry is left alone until the next explicit trigger.
 *
 *  Not a discard — nothing is ever deleted for failing (see sweepExpired). This
 *  only stops the automatic loop from hammering a link on an upload that is
 *  reliably failing, so the other seven files behind it get a turn. */
const MAX_AUTOMATIC_ATTEMPTS = 5

/** True when an error means "try again later" rather than "this will never
 *  work".
 *
 *  The distinction decides whether the entry stays queued. A network failure,
 *  a 5xx or a 429 are all the connection being bad, which is the case this
 *  story exists for. A 4xx from our API — the case was deleted, the caller lost
 *  access, the file is too large — will fail identically forever, and retrying
 *  it every reconnect would be a queue that never drains and a pending count
 *  that never reaches zero.
 *
 *  Anything unrecognised is treated as transient. Keeping a file we cannot send
 *  costs disk; discarding one we could have sent costs the user's work. */
function isTransient(error: unknown): boolean {
  if (error instanceof TypeError) return true // fetch's offline failure
  const status = (error as { status?: number } | null)?.status
  if (typeof status !== 'number') return true
  if (status === 429) return true
  return status >= 500
}

function messageFor(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/** Sends one entry through the three-step, resuming wherever it left off.
 *
 *  Returns true when the file is recorded on the server and the entry can go. */
async function sendOne(entry: QueuedUpload): Promise<boolean> {
  // (1) A ticket, unless a previous attempt already won one.
  //
  // Reusing the stored key is what makes an interrupted upload resumable rather
  // than restarted. It is safe because the commit re-verifies the key against
  // the case (AD-18), and because a stale key that storage has since expired
  // fails at the PUT, which is a transient failure that re-signs next time.
  let objectKey = entry.objectKey
  if (!objectKey) {
    const ticket = await api.post<UploadTicket>(
      `/cases/${entry.caseId}/files/upload-url`,
      { fileName: entry.fileName, contentType: entry.contentType },
    )
    objectKey = ticket.objectKey

    // (2) The bytes, straight to the bucket — never through our API (AD-18).
    const put = await fetch(ticket.url, {
      method: ticket.method,
      body: entry.file,
      headers: entry.contentType ? { 'Content-Type': entry.contentType } : undefined,
    })
    if (!put.ok) {
      // Thrown with the status attached so isTransient can read it. A 403 here
      // is an expired signature, which IS transient — the next attempt signs
      // afresh — so the key is deliberately not persisted on this path.
      throw Object.assign(new Error(`storage refused the upload (${put.status})`), {
        status: put.status >= 400 && put.status < 500 ? 503 : put.status,
      })
    }

    // The bytes are in the bucket. Persist the key BEFORE committing, so that a
    // browser killed between the PUT and the commit resumes at the commit
    // rather than re-sending the file.
    entry.objectKey = objectKey
    await recordAttempt(entry)
  }

  // (3) Record it, carrying the idempotency key (AD-18). This is the request
  // whose ambiguous failure the key exists to make safe.
  await api.post<FileResponse>(`/cases/${entry.caseId}/files`, {
    objectKey,
    fileName: entry.fileName,
    contentType: entry.contentType,
    // The kind the uploader chose, carried from the queue rather than from
    // anything on screen: this send can be days after the pick, on a device
    // that has been closed and reopened. Absent on an entry written before the
    // picker existed, which the server files as 'other'.
    docKind: entry.docKind,
    clientKey: entry.clientKey,
  })

  return true
}

/** Sends everything queued, oldest first.
 *
 *  Serial, not parallel. Eight concurrent PUTs on the connection this story
 *  assumes is how all eight time out; one at a time is slower and finishes. */
async function drain(onCommitted: (caseId: string) => void): Promise<void> {
  if (!queueStorageAvailable()) return
  if (draining) {
    drainRequested = true
    return
  }
  draining = true

  try {
    do {
      drainRequested = false

      // Expiry is swept here rather than on a timer: the queue is only
      // interesting when something is being sent, and a timer would keep a
      // sleeping tab awake to delete files nobody is waiting for.
      await sweepExpired()

      const entries = await listAll()
      await refresh()

      for (const entry of entries) {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) break
        if (entry.attempts >= MAX_AUTOMATIC_ATTEMPTS) continue

        entry.attempts += 1
        try {
          await sendOne(entry)
          // Removed only now — after the server has the row. Removing on the
          // attempt would lose the file whenever the commit failed.
          await remove(entry.clientKey)
          onCommitted(entry.caseId)
        } catch (error) {
          entry.lastError = messageFor(error)
          if (isTransient(error)) {
            // Stays queued. This is the whole promise of the feature.
            await recordAttempt(entry)
          } else {
            // Permanently rejected: the case is gone, or access was revoked.
            // Keeping it would mean a pending count that can never reach zero,
            // which trains the user to ignore the one thing they must not.
            await remove(entry.clientKey)
          }
        }
        await refresh()
      }
    } while (drainRequested)
  } finally {
    draining = false
  }
}

/** Every drain and every mount restore still in flight.
 *
 *  Held only so a test can wait for the singleton to go quiet. The loop outlives
 *  the component that started it — that is its job — so it also outlives the
 *  TEST that started it: a drain still running when one test ends carries on
 *  into the next one's `beforeEach`, sees the device flipped back online and the
 *  API stub reset, fails transiently and `recordAttempt`s the previous test's
 *  file into the freshly deleted database (issue #72, "expected 2 to be 1"). */
const inFlight = new Set<Promise<unknown>>()

function track(work: Promise<unknown>): void {
  inFlight.add(work)
  // `finally`, so a rejection still surfaces exactly as an untracked one did.
  void work.finally(() => inFlight.delete(work))
}

/** Resolves once no drain or mount restore is running. Tests only —
 *  production never waits for the queue; it is the queue that waits. */
export async function settleUploadQueueForTests(): Promise<void> {
  // A loop, not one await: a restore that settles starts a drain, and a drain
  // asked for mid-flight runs once more.
  while (inFlight.size > 0) {
    await Promise.allSettled([...inFlight])
  }
}

/* ── the hook ─────────────────────────────────────────────────────────────── */

/** The queue, for one case's file area.
 *
 *  Returns the entries pending on THIS case (what the area renders) while the
 *  drain it starts covers every case (what the AC requires). */
export function useUploadQueue(caseId: string) {
  const queryClient = useQueryClient()
  const all = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  /* Invalidation is routed through a ref so the drain loop — which outlives any
   * one render — never closes over a stale queryClient.
   *
   * Assigned in an effect rather than during render: writing a ref while
   * rendering is a side effect in what must be a pure function, and React may
   * render without committing (StrictMode does exactly that), which would
   * publish a client from a render that never happened. */
  const invalidate = useRef<(committedCaseId: string) => void>(() => {})
  useEffect(() => {
    invalidate.current = (committedCaseId: string) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cases.files(committedCaseId),
      })
    }
  }, [queryClient])

  const run = useCallback(() => {
    track(drain((committed) => invalidate.current(committed)))
  }, [])

  useEffect(() => {
    // Restore on mount. This is the AC's "close and reopen the application":
    // the queue is read back from disk and its pending count reappears before
    // the user has to ask where their files went.
    track(refresh().then(run))

    // And re-send on reconnect, by itself, with nobody pressing anything.
    const onOnline = () => run()
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [run])

  /** Accepts files: persisted first, sent second.
   *
   *  The ordering is the durability guarantee. Writing to disk before the first
   *  network call means a browser killed mid-upload has already recorded the
   *  work — the opposite order would lose exactly the files added during the
   *  outage this feature is for. */
  const add = useCallback(
    async (files: File[], docKind?: string): Promise<void> => {
      for (const file of files) {
        try {
          await enqueue(caseId, file, newClientKey(), docKind)
        } catch {
          // No durable storage on this device. The file is still sent below on
          // the next drain pass only if it was queued, so fall back to a direct
          // attempt rather than dropping it silently.
          await sendOne({
            clientKey: newClientKey(),
            caseId,
            file,
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            sizeBytes: file.size,
            docKind,
            queuedAt: Date.now(),
            attempts: 1,
            /* Zero, because on this path the field has no work to do. `sequence`
               orders entries READ BACK from disk, where a shared millisecond
               leaves the order to IndexedDB. This entry is never written and
               never sorted: it is handed straight to sendOne, and the loop above
               is what orders these files. */
            sequence: 0,
          }).then(
            () => invalidate.current(caseId),
            () => {},
          )
        }
      }
      await refresh()
      run()
    },
    [caseId, run],
  )

  const forCase = all.filter((entry) => entry.caseId === caseId)

  return {
    /** This case's pending uploads, oldest first — the inspectable queue. */
    pending: forCase,
    /** The pending count UX-DR-19 asks to be visible. */
    pendingCount: forCase.length,
    /** Pending anywhere, so a case screen can say work is outstanding
     *  elsewhere rather than letting the app look idle. */
    pendingCountAllCases: all.length,
    add,
    /** Sends now, for the "try again" affordance. */
    retryNow: run,
  }
}

/** Reads one case's queue straight from storage, bypassing the subscription.
 *  Exported for tests and for any surface that needs a count before mounting
 *  the file area. */
export async function readQueueForCase(caseId: string): Promise<QueuedUpload[]> {
  return listForCase(caseId)
}
